import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import { Package, MapPin, DollarSign, TrendingUp, Power, PowerOff, Navigation, Clock, CheckCircle, AlertCircle, LogOut, RefreshCw, Home, Calendar, History, Menu, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import io from 'socket.io-client';
import { useAuth } from '../../contexts/AuthContext';
import './Dashboard.css';

const Dashboard = () => {
  const [kmEntrada, setKmEntrada] = useState('');
  const [kmSaida, setKmSaida] = useState('');
  const [registroKM, setRegistroKM] = useState(null);
  const [modalPagamento, setModalPagamento] = useState(null);
  const [modalEncerrarDiaria, setModalEncerrarDiaria] = useState(false);
  const [modalIniciarDiaria, setModalIniciarDiaria] = useState(false);
  const [kmSaidaDiaria, setKmSaidaDiaria] = useState('');
  const [kmEntradaInicial, setKmEntradaInicial] = useState('');
  const [pagamentoData, setPagamentoData] = useState({ valorRecebido: '', formaPagamentoRecebida: 'dinheiro' });
  const [pagamentosSequenciais, setPagamentosSequenciais] = useState([]); // ✅ NOVO: Array de pagamentos múltiplos
  const [novoPagamento, setNovoPagamento] = useState({ tipo: 'dinheiro', valor: 0, valorRecebido: 0, troco: 0 }); // ✅ NOVO: Pagamento sendo adicionado
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(new Date());
  const [activeTab, setActiveTab] = useState('dashboard');
  const [menuAberto, setMenuAberto] = useState(false);
  const queryClient = useQueryClient();
  const { logout } = useAuth();

  // Buscar perfil do entregador
  const { data: entregador, isLoading: loadingEntregador, error: errorEntregador } = useQuery(
    'entregador-perfil',
    async () => {
      try {
        const response = await api.get('/api/entregadores/me');
        return response.data.data.entregador;
      } catch (error) {
        console.error('Erro ao buscar perfil do entregador:', error.response?.data || error.message);
        throw error;
      }
    },
    {
      retry: false,
      refetchOnWindowFocus: false, // Evitar refetch automático
      onError: (error) => {
        if (error.response?.status === 404) {
          console.error('Entregador não encontrado. O usuário precisa ser associado a um entregador no sistema.');
        } else if (error.response?.status === 500) {
          console.error('Erro interno do servidor:', error.response?.data);
          toast.error('Erro ao carregar perfil. Tente novamente mais tarde.');
        }
      }
    }
  );

  // Buscar pedidos disponíveis
  const { data: pedidosDisponiveis, refetch: refetchDisponiveis, isLoading: loadingPedidos } = useQuery(
    'pedidos-disponiveis',
    async () => {
      try {
        const response = await api.get('/api/entregadores/entregas/disponiveis');
        const pedidos = response.data.data.pedidos || [];
        setUltimaAtualizacao(new Date());
        return pedidos;
      } catch (error) {
        console.error('❌ Erro ao buscar pedidos disponíveis:', error);
        return [];
      }
    },
    {
      refetchInterval: 5000, // Atualizar a cada 5 segundos
      enabled: entregador?.online === true && !!entregador?._id,
      retry: 3,
      retryDelay: 1000,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      onSuccess: () => {
        setUltimaAtualizacao(new Date());
      }
    }
  );

  // Função para refresh manual
  const handleRefresh = () => {
    refetchDisponiveis();
    refetchEntregas();
    queryClient.invalidateQueries('entregador-perfil');
    toast.success('Atualizando...');
  };

  // Buscar minhas entregas
  const { data: minhasEntregas, refetch: refetchEntregas } = useQuery(
    'minhas-entregas',
    async () => {
      const response = await api.get('/api/entregadores/me/pedidos');
      return response.data.data.entregas || [];
    },
    {
      refetchInterval: 5000
    }
  );

  // Buscar registro de KM de hoje (vem junto com o perfil)
  const { data: perfilCompleto } = useQuery(
    ['entregador-perfil-completo', entregador?._id],
    async () => {
      if (!entregador?._id) return null;
      try {
        const response = await api.get('/api/entregadores/me');
        return response.data.data;
      } catch (error) {
        console.error('Erro ao buscar perfil completo:', error);
        return null;
      }
    },
    {
      enabled: !!entregador?._id,
      refetchInterval: 30000
    }
  );

  // Buscar diária de hoje (PRIMEIRO, antes de usar nos useEffects)
  const { data: diariaHoje, refetch: refetchDiaria } = useQuery(
    ['diaria-hoje', entregador?._id],
    async () => {
      if (!entregador?._id) return null;
      try {
        const response = await api.get('/api/entregadores/me/diaria/hoje');
        return response.data.data?.diaria || null;
      } catch (error) {
        return null;
      }
    },
    {
      enabled: !!entregador?._id,
      refetchInterval: 30000
    }
  );

  // Atualizar registroKM quando o perfil completo for carregado
  useEffect(() => {
    // Se perfilCompleto foi carregado (não é null/undefined)
    if (perfilCompleto !== undefined) {
      if (perfilCompleto?.registroKM) {
        // Se tem registroKM, atualizar o estado
        setRegistroKM(perfilCompleto.registroKM);
      } else {
        // Se não tem registroKM (null ou undefined), limpar o estado
        // Isso garante que o botão "Iniciar Diária" apareça quando não houver registroKM de hoje
        setRegistroKM(null);
      }
    }
  }, [perfilCompleto]);

  // Conectar Socket.io para atualizações em tempo real
  useEffect(() => {
    // Usar mesma lógica do api.js para determinar URL
    let socketURL;
    if (process.env.NODE_ENV === 'production') {
      socketURL = typeof window !== 'undefined' ? window.location.origin : '';
    } else {
      socketURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    }

    const socket = io(socketURL, {
      transports: ['websocket', 'polling'],
      path: '/socket.io/'
    });
    
    socket.on('connect', () => {
      // Conectado ao Socket.io
    });

    // Quando um pedido ficar disponível (Confirmado, Em preparo ou Pronto)
    socket.on('pedido-disponivel-entrega', (data) => {
      const statusMsg = {
        'Confirmado': 'foi confirmado',
        'Em preparo': 'está em preparo',
        'Pronto': 'está pronto'
      };
      toast.success(`Pedido #${data.numero} ${statusMsg[data.status] || 'disponível'}!`, {
        duration: 5000,
        icon: '📦'
      });
      // Atualizar lista de pedidos disponíveis
      refetchDisponiveis();
    });

    // Quando um novo pedido for criado
    socket.on('novo-pedido-entrega', (data) => {
      if (entregador?.online) {
        // Aguardar um pouco antes de atualizar para garantir que o status foi atualizado
        setTimeout(() => {
          refetchDisponiveis();
        }, 1000);
      }
    });

    // Quando um pedido ficar pronto (compatibilidade)
    socket.on('pedido-pronto-entrega', (data) => {
      toast.success(`Pedido #${data.numero} está pronto para entrega!`, {
        duration: 5000,
        icon: '📦'
      });
      refetchDisponiveis();
    });

    // Quando a diária for paga pelo admin
    socket.on('diaria-paga', (data) => {
      // Verificar se é a diária deste entregador
      if (data.entregadorId === entregador?._id) {
        toast.success('💰 Diária paga com sucesso!', {
          duration: 5000,
          icon: '✅'
        });
        refetchDiaria();
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [entregador?.online, refetchDisponiveis, refetchDiaria]);

  // Mutation para atualizar status online/offline
  const toggleStatusMutation = useMutation(
    (online) => api.patch('/api/entregadores/me/status', { online }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('entregador-perfil');
        toast.success(entregador?.online ? 'Você está offline' : 'Você está online');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao atualizar status');
      }
    }
  );

  // Mutation para registrar KM
  const registrarKMMutation = useMutation(
    (data) => api.post('/api/entregadores/me/km', data),
    {
      onSuccess: async (response) => {
        toast.success('KM registrado com sucesso!');
        setKmEntrada('');
        setKmSaida('');
        // Atualizar registroKM diretamente com a resposta
        if (response.data?.data?.registroKM) {
          setRegistroKM(response.data.data.registroKM);
        }
        // Invalidar e refetch queries para garantir sincronização
        await queryClient.invalidateQueries('entregador-perfil');
        await queryClient.invalidateQueries('entregador-perfil-completo');
        await queryClient.refetchQueries('entregador-perfil-completo');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao registrar KM');
      }
    }
  );

  // Mutation para encerrar diária
  const encerrarDiariaMutation = useMutation(
    (data) => api.post('/api/entregadores/me/diaria/encerrar', data),
    {
      onSuccess: async (response) => {
        toast.success('Diária encerrada com sucesso!');
        setModalEncerrarDiaria(false);
        setKmSaidaDiaria('');
        // Invalidar todas as queries para atualizar os dados
        await queryClient.invalidateQueries('diaria-hoje');
        await queryClient.invalidateQueries('entregador-perfil');
        await queryClient.invalidateQueries('entregador-perfil-completo');
        await queryClient.invalidateQueries('registro-km-hoje');
        await queryClient.invalidateQueries('minhas-entregas');
        // Refetch para garantir que os valores sejam zerados
        await queryClient.refetchQueries('minhas-entregas');
        await queryClient.refetchQueries('entregador-perfil-completo');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao encerrar diária');
      }
    }
  );

  const handleIniciarDiaria = () => {
    if (!kmEntradaInicial || isNaN(kmEntradaInicial)) {
      toast.error('Digite um valor válido para KM de entrada');
      return;
    }
    registrarKMMutation.mutate({ 
      kmEntrada: parseFloat(kmEntradaInicial)
    });
    setModalIniciarDiaria(false);
    setKmEntradaInicial('');
  };

  const handleEncerrarDiaria = () => {
    if (!kmSaidaDiaria || isNaN(kmSaidaDiaria)) {
      toast.error('Digite um valor válido para KM de saída');
      return;
    }
    encerrarDiariaMutation.mutate({
      kmSaida: parseFloat(kmSaidaDiaria),
      observacoes: ''
    });
  };

  // Mutation para aceitar entrega
  const aceitarEntregaMutation = useMutation(
    (pedidoId) => api.post(`/api/entregadores/entregas/${pedidoId}/aceitar`),
    {
      onSuccess: () => {
        toast.success('Entrega aceita com sucesso!');
        queryClient.invalidateQueries('pedidos-disponiveis');
        queryClient.invalidateQueries('minhas-entregas');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao aceitar entrega');
      }
    }
  );

  // Mutation para atualizar status da entrega
  const atualizarStatusMutation = useMutation(
    ({ entregaId, status }) => api.patch(`/api/entregadores/entregas/${entregaId}/status`, { status }),
    {
      onSuccess: (data, variables) => {
        if (variables.status === 'entregue') {
          // Se o pedido foi pago online, não precisa registrar pagamento
          const entrega = minhasEntregas?.find(e => e._id === variables.entregaId);
          const pedido = entrega?.pedido;
          
          if (pedido?.statusPagamento === 'Pago' || pedido?.formaPagamento !== 'dinheiro') {
            toast.success('Entrega confirmada!');
            queryClient.invalidateQueries('minhas-entregas');
          } else {
            // Abrir modal de pagamento
            setModalPagamento(entrega);
            setPagamentoData({ 
              valorRecebido: pedido?.total?.toFixed(2) || '', 
              formaPagamentoRecebida: 'dinheiro' 
            });
            // ✅ Resetar pagamentos sequenciais
            setPagamentosSequenciais([]);
            setNovoPagamento({ tipo: 'dinheiro', valor: pedido?.total || 0, valorRecebido: pedido?.total || 0, troco: 0 });
          }
        } else {
          toast.success('Status atualizado!');
          queryClient.invalidateQueries('minhas-entregas');
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao atualizar status');
      }
    }
  );

  // Mutation para registrar pagamento
  const registrarPagamentoMutation = useMutation(
    ({ entregaId, data }) => api.post(`/api/entregadores/entregas/${entregaId}/pagamento`, data),
    {
      onSuccess: async () => {
        toast.success('Pagamento registrado com sucesso!');
        setModalPagamento(null);
        setPagamentoData({ valorRecebido: '', formaPagamentoRecebida: 'dinheiro' });
        setPagamentosSequenciais([]); // ✅ Limpar pagamentos sequenciais
        setNovoPagamento({ tipo: 'dinheiro', valor: 0, valorRecebido: 0, troco: 0 });
        // ✅ Invalidar e refetch imediatamente para atualizar o status
        await queryClient.invalidateQueries('minhas-entregas');
        await queryClient.refetchQueries('minhas-entregas');
        queryClient.invalidateQueries('caixa-atual');
        queryClient.invalidateQueries('pedidos-disponiveis');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao registrar pagamento');
      }
    }
  );

  const handleToggleStatus = () => {
    toggleStatusMutation.mutate(!entregador?.online);
  };

  const handleRegistrarKM = (tipo) => {
    if (tipo === 'entrada') {
      if (!kmEntrada || isNaN(kmEntrada)) {
        toast.error('Digite um valor válido para KM de entrada');
        return;
      }
      registrarKMMutation.mutate({ kmEntrada: parseFloat(kmEntrada) });
    } else {
      if (!kmSaida || isNaN(kmSaida)) {
        toast.error('Digite um valor válido para KM de saída');
        return;
      }
      registrarKMMutation.mutate({ 
        kmEntrada: registroKM?.kmEntrada || kmEntrada,
        kmSaida: parseFloat(kmSaida) 
      });
    }
  };

  if (loadingEntregador) {
    return <div className="loading">Carregando...</div>;
  }

  if (errorEntregador || !entregador) {
    const is404 = errorEntregador?.response?.status === 404;
    return (
      <div className="entregador-error">
        <AlertCircle size={64} />
        <h2>Entregador não encontrado</h2>
        {is404 ? (
          <>
            <p>Seu usuário ainda não foi associado a um perfil de entregador no sistema.</p>
            <p style={{ marginTop: '1rem', color: '#718096' }}>
              Por favor, entre em contato com o administrador para que ele cadastre seu perfil de entregador.
            </p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#a0aec0' }}>
              Email: {errorEntregador?.config?.headers?.Authorization ? 'Você está logado' : 'Faça login primeiro'}
            </p>
          </>
        ) : (
          <p>Erro ao carregar perfil. Tente recarregar a página ou entre em contato com o suporte.</p>
        )}
      </div>
    );
  }

  // Calcular estatísticas do dia atual
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const amanha = new Date(hoje);
  amanha.setDate(amanha.getDate() + 1);

  // Filtrar entregas do dia atual
  const entregasHoje = (minhasEntregas || []).filter(e => {
    if (!e) return false;
    
    // Tentar usar data do pedido primeiro, depois data da entrega, depois createdAt
    let dataParaComparar;
    if (e.pedido?.createdAt) {
      dataParaComparar = new Date(e.pedido.createdAt);
    } else if (e.dataEntrega) {
      dataParaComparar = new Date(e.dataEntrega);
    } else if (e.createdAt) {
      dataParaComparar = new Date(e.createdAt);
    } else {
      return false; // Sem data, não incluir
    }
    
    // Normalizar data para comparação (remover horas)
    const dataNormalizada = new Date(dataParaComparar);
    dataNormalizada.setHours(0, 0, 0, 0);
    
    // Comparar apenas a data (ignorar horas)
    const hojeNormalizado = new Date(hoje);
    hojeNormalizado.setHours(0, 0, 0, 0);
    
    return dataNormalizada.getTime() === hojeNormalizado.getTime();
  });

  // Filtrar apenas entregas entregues (status 'entregue')
  const entregasEntregues = entregasHoje.filter(e => {
    const status = e.status?.toLowerCase() || '';
    return status === 'entregue';
  });
  
  // Calcular valores totais - usar valorRecebido se existir, senão usar total do pedido
  const totalRecebido = entregasEntregues.reduce((acc, e) => {
    let valor = 0;
    if (e.valorRecebido && e.valorRecebido > 0) {
      valor = typeof e.valorRecebido === 'number' ? e.valorRecebido : parseFloat(e.valorRecebido) || 0;
    } else if (e.pedido?.total) {
      valor = typeof e.pedido.total === 'number' ? e.pedido.total : parseFloat(e.pedido.total) || 0;
    }
    return acc + valor;
  }, 0);
  
  // Calcular comissão total
  const totalComissao = entregasEntregues.reduce((acc, e) => {
    const comissao = e.comissao || 0;
    return acc + (typeof comissao === 'number' ? comissao : parseFloat(comissao) || 0);
  }, 0);

  return (
    <div className="entregador-dashboard">
      {/* Botão Menu Hambúrguer (Mobile) */}
      <button 
        className="menu-hamburger"
        onClick={() => setMenuAberto(!menuAberto)}
        aria-label="Menu"
      >
        {menuAberto ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Overlay (Mobile) */}
      {menuAberto && (
        <div 
          className="menu-overlay"
          onClick={() => setMenuAberto(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`entregador-sidebar ${menuAberto ? 'aberto' : ''}`}>
        <div className="sidebar-header">
          <h3>🚴 Menu</h3>
        </div>
        <nav className="sidebar-nav">
          <button
            className={`sidebar-item ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('dashboard');
              setMenuAberto(false); // Fechar menu ao clicar (mobile)
            }}
          >
            <Home size={20} />
            <span>Dashboard</span>
          </button>
          <button
            className={`sidebar-item ${activeTab === 'entregas' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('entregas');
              setMenuAberto(false); // Fechar menu ao clicar (mobile)
            }}
          >
            <Package size={20} />
            <span>Entregas</span>
          </button>
          <button
            className={`sidebar-item ${activeTab === 'historico' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('historico');
              setMenuAberto(false); // Fechar menu ao clicar (mobile)
            }}
          >
            <History size={20} />
            <span>Histórico</span>
          </button>
        </nav>
      </div>

      {/* Main Content */}
      <div className="entregador-main-content">
      {/* Header */}
      <div className="entregador-header">
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2>🚴 Dashboard do Entregador</h2>
          <p>Bem-vindo, {entregador.usuario?.nome || 'Entregador'}</p>
          <p style={{ fontSize: '0.75rem', color: '#718096', marginTop: '4px' }}>
            Última atualização: {format(ultimaAtualizacao, 'HH:mm:ss')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            className="btn btn-secondary"
            onClick={handleRefresh}
            title="Atualizar"
            style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
          >
            <RefreshCw size={18} />
            <span>Atualizar</span>
          </button>
          <button
            className={`status-toggle ${entregador.online ? 'online' : 'offline'}`}
            onClick={handleToggleStatus}
            disabled={toggleStatusMutation.isLoading}
          >
            {entregador.online ? (
              <>
                <Power size={20} />
                <span>Online</span>
              </>
            ) : (
              <>
                <PowerOff size={20} />
                <span>Offline</span>
              </>
            )}
          </button>
          <button
            className="btn btn-danger"
            onClick={() => {
              if (window.confirm('Deseja realmente sair?')) {
                logout();
              }
            }}
            title="Sair"
            style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
          >
            <LogOut size={18} />
            <span>Sair</span>
          </button>
        </div>
      </div>

      {/* Conteúdo baseado na aba ativa */}
      {activeTab === 'dashboard' && (
        <>
          {/* Cards de Estatísticas */}
          <div className="entregador-stats">
            <div className="stat-card">
              <div className="stat-icon blue">
                <Package size={24} />
              </div>
              <div className="stat-info">
                <h3>{entregasHoje.length}</h3>
                <p>Entregas Hoje</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon green">
                <CheckCircle size={24} />
              </div>
              <div className="stat-info">
                <h3>{entregasEntregues.length}</h3>
                <p>Entregues</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon purple">
                <DollarSign size={24} />
              </div>
              <div className="stat-info">
                <h3>R$ {totalRecebido.toFixed(2)}</h3>
                <p>Total Recebido</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon orange">
                <TrendingUp size={24} />
              </div>
              <div className="stat-info">
                <h3>R$ {totalComissao.toFixed(2)}</h3>
                <p>Comissão Hoje</p>
              </div>
            </div>
          </div>

          {/* Botão Iniciar/Encerrar Diária */}
          {(!registroKM || !registroKM.kmEntrada || (registroKM.kmEntrada && !registroKM.kmSaida && !diariaHoje)) && (
            <div className="entregador-section" style={{ 
              background: registroKM?.kmEntrada && !registroKM?.kmSaida 
                ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '2rem',
              borderRadius: '12px',
              textAlign: 'center',
              marginBottom: '2rem',
              boxShadow: registroKM?.kmEntrada && !registroKM?.kmSaida
                ? '0 4px 12px rgba(245, 158, 11, 0.3)'
                : '0 4px 12px rgba(102, 126, 234, 0.3)'
            }}>
              <h3 style={{ marginBottom: '1rem', color: 'white', fontSize: '1.5rem' }}>
                {registroKM?.kmEntrada && !registroKM?.kmSaida ? '🏁 Encerrar Diária' : '🚀 Iniciar Diária'}
              </h3>
              <p style={{ marginBottom: '1.5rem', opacity: 0.9, fontSize: '1rem' }}>
                {registroKM?.kmEntrada && !registroKM?.kmSaida
                  ? 'Clique no botão abaixo para encerrar sua diária, informar o KM de saída e gerar seus pagamentos'
                  : 'Clique no botão abaixo para iniciar sua diária e começar a receber entregas'}
              </p>
              <button
                className="btn btn-primary"
                onClick={() => {
                  if (registroKM?.kmEntrada && !registroKM?.kmSaida) {
                    // Se já tem KM entrada, abrir modal de encerrar
                    setModalEncerrarDiaria(true);
                  } else {
                    // Se não tem KM entrada, abrir modal de iniciar
                    setModalIniciarDiaria(true);
                  }
                }}
                style={{
                  padding: '1rem 2rem',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  background: 'white',
                  color: registroKM?.kmEntrada && !registroKM?.kmSaida ? '#f59e0b' : '#667eea',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                {registroKM?.kmEntrada && !registroKM?.kmSaida ? '🏁 Encerrar Diária' : '🚀 Iniciar Diária'}
              </button>
            </div>
          )}

          {/* Card Financeiro - Valores a Receber (mostrar no Dashboard quando diária encerrada) */}
          {diariaHoje && (
            <div className="diaria-card" style={{ 
              marginBottom: '2rem', 
              padding: '1.5rem', 
              background: '#f0f9ff', 
              borderRadius: '8px', 
              border: '1px solid #bae6fd' 
            }}>
              <h4 style={{ marginBottom: '1rem', color: '#0369a1', fontSize: '1.25rem' }}>💰 Financeiro - Valores a Receber</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {/* Valores Detalhados */}
                <div style={{ padding: '0.75rem', background: '#ffffff', borderRadius: '6px', border: '1px solid #e0f2fe' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#64748b' }}>Valor da Diária:</span>
                    <strong style={{ color: '#0369a1' }}>R$ {(diariaHoje.valorDiaria || 0).toFixed(2)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#64748b' }}>Valor do KM ({(diariaHoje.kmRodados || 0)} km):</span>
                    <strong style={{ color: '#0369a1' }}>R$ {(diariaHoje.valorKM || 0).toFixed(2)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#64748b' }}>Comissões ({(diariaHoje.quantidadeEntregas || 0)} entregas):</span>
                    <strong style={{ color: '#0369a1' }}>R$ {(diariaHoje.totalComissoes || 0).toFixed(2)}</strong>
                  </div>
                  <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '2px solid #bae6fd', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#0369a1', fontWeight: 'bold', fontSize: '1.1rem' }}>Total a Receber:</span>
                    <strong style={{ fontSize: '1.5rem', color: '#0369a1', fontWeight: 'bold' }}>R$ {(diariaHoje.totalReceber || 0).toFixed(2)}</strong>
                  </div>
                </div>
                
                {/* Status */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#64748b' }}>Status:</span>
                  <strong className={diariaHoje.status === 'pago' ? 'status-pago' : 'status-pendente'} style={{ 
                    color: diariaHoje.status === 'pago' ? '#10b981' : '#f59e0b',
                    fontWeight: 'bold',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    background: diariaHoje.status === 'pago' ? '#d1fae5' : '#fef3c7'
                  }}>
                    {diariaHoje.status === 'pago' ? '✅ Pago' : '⏳ Aguardando Pagamento'}
                  </strong>
                </div>
                {diariaHoje.status === 'pago' && diariaHoje.dataPagamento && (
                  <div style={{ fontSize: '0.875rem', color: '#64748b', textAlign: 'center' }}>
                    Pago em: {new Date(diariaHoje.dataPagamento).toLocaleDateString('pt-BR')}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Pedidos Disponíveis */}
          {entregador.online ? (
            <div className="entregador-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3>📦 Pedidos Disponíveis</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {loadingPedidos && (
                    <span style={{ fontSize: '0.875rem', color: '#718096', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
                      Atualizando...
                    </span>
                  )}
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={handleRefresh}
                    disabled={loadingPedidos}
                    style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                  >
                    <RefreshCw size={14} />
                  </button>
                </div>
              </div>
              {pedidosDisponiveis && pedidosDisponiveis.length > 0 ? (
                <div className="pedidos-grid">
                  {pedidosDisponiveis.map((pedido) => {
                    const statusColors = {
                      'Confirmado': '#3B82F6',
                      'Em preparo': '#F59E0B',
                      'Pronto': '#10B981'
                    };
                    const statusColor = statusColors[pedido.status] || '#6B7280';
                    
                    return (
                      <div key={pedido._id} className="pedido-card">
                        <div className="pedido-header">
                          <div>
                            <strong>Pedido #{pedido.numero}</strong>
                            <span 
                              className="pedido-status-badge" 
                              style={{ 
                                backgroundColor: statusColor,
                                color: 'white',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                marginLeft: '8px'
                              }}
                            >
                              {pedido.status}
                            </span>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <span className="pedido-total">R$ {pedido.total?.toFixed(2)}</span>
                            <span style={{ fontSize: '0.75rem', color: pedido.taxaEntrega > 0 ? '#059669' : '#718096', marginTop: '2px', fontWeight: pedido.taxaEntrega > 0 ? '600' : '400' }}>
                              {pedido.taxaEntrega > 0 ? `💰 Taxa: R$ ${pedido.taxaEntrega.toFixed(2)}` : 'Sem taxa'}
                            </span>
                          </div>
                        </div>
                        <div className="pedido-info">
                          <div style={{ marginBottom: '8px' }}>
                            <p style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                              <MapPin size={16} /> Endereço:
                            </p>
                            <p style={{ marginLeft: '20px', marginBottom: '2px' }}>
                              {pedido.enderecoEntrega?.rua || 'Rua não informada'}, {pedido.enderecoEntrega?.numero || 'S/N'}
                            </p>
                            {pedido.enderecoEntrega?.bairro && (
                              <p style={{ marginLeft: '20px', marginBottom: '2px', color: '#718096' }}>
                                {pedido.enderecoEntrega.bairro}
                              </p>
                            )}
                            {pedido.enderecoEntrega?.referencia && (
                              <p style={{ marginLeft: '20px', marginBottom: '2px', color: '#9CA3AF', fontSize: '0.875rem', fontStyle: 'italic' }}>
                                📍 Ref: {pedido.enderecoEntrega.referencia}
                              </p>
                            )}
                            {pedido.enderecoEntrega?.cidade && pedido.enderecoEntrega?.estado && (
                              <p style={{ marginLeft: '20px', color: '#718096', fontSize: '0.875rem' }}>
                                {pedido.enderecoEntrega.cidade}/{pedido.enderecoEntrega.estado}
                              </p>
                            )}
                          </div>
                          <p><Clock size={16} /> {pedido.tempoEstimado} min</p>
                          {pedido.status === 'Pronto' && (
                            <p style={{ color: '#10B981', fontWeight: 'bold', marginTop: '8px' }}>
                              ✅ Pronto para entrega!
                            </p>
                          )}
                        </div>
                        <button
                          className="btn btn-primary btn-block"
                          onClick={() => aceitarEntregaMutation.mutate(pedido._id)}
                          disabled={aceitarEntregaMutation.isLoading}
                        >
                          Aceitar Entrega
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="empty-state">
                  <Package size={64} />
                  <p>Nenhum pedido disponível no momento</p>
                  <p style={{ fontSize: '0.9rem', color: '#718096', marginTop: '8px' }}>
                    Pedidos aparecerão aqui quando estiverem Confirmados, Em preparo ou Prontos
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="entregador-section">
              <div style={{ textAlign: 'center', padding: '2rem', color: '#718096' }}>
                <PowerOff size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                <p>Você está offline. Ative o status para receber pedidos.</p>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'entregas' && (
        <>
          {/* Minhas Entregas */}
          <div className="entregador-section">
            <h3>🚚 Minhas Entregas</h3>
            {minhasEntregas && minhasEntregas.length > 0 ? (
              <div className="entregas-lista">
                {minhasEntregas.map((entrega) => (
                  <div key={entrega._id} className="entrega-card">
                    <div className="entrega-header">
                      <strong>Pedido #{entrega.pedido?.numero}</strong>
                      <span className={`status-badge status-${entrega.status}`}>
                        {entrega.status === 'pendente' && 'Pendente'}
                        {entrega.status === 'aceita' && 'Aceita'}
                        {entrega.status === 'saiu' && 'Saiu para entrega'}
                        {entrega.status === 'chegou' && 'Chegou no endereço'}
                        {entrega.status === 'entregue' && 'Entregue'}
                        {entrega.status === 'cancelada' && 'Cancelada'}
                      </span>
                    </div>
                    <div className="entrega-info">
                      <p><MapPin size={16} /> {entrega.pedido?.enderecoEntrega?.rua}, {entrega.pedido?.enderecoEntrega?.numero}</p>
                      <div style={{ marginTop: '0.5rem' }}>
                        <p style={{ marginBottom: '0.25rem' }}>
                          <DollarSign size={16} /> 
                          <span style={{ marginLeft: '0.5rem' }}>Total: R$ {entrega.pedido?.total?.toFixed(2)}</span>
                        </p>
                        <p style={{ fontSize: '0.875rem', color: entrega.pedido?.taxaEntrega > 0 ? '#059669' : '#6b7280', marginLeft: '1.5rem', fontWeight: entrega.pedido?.taxaEntrega > 0 ? '600' : '400' }}>
                          {entrega.pedido?.taxaEntrega > 0 ? `💰 Taxa de entrega: R$ ${entrega.pedido.taxaEntrega.toFixed(2)}` : 'Sem taxa de entrega'}
                        </p>
                      </div>
                      {/* ✅ Exibir status de pagamento */}
                      {entrega.pedido?.statusPagamento && (
                        <p style={{ 
                          marginTop: '0.5rem', 
                          padding: '0.25rem 0.5rem', 
                          borderRadius: '0.25rem',
                          background: entrega.pedido.statusPagamento === 'Pago' ? '#d1fae5' : '#fef3c7',
                          color: entrega.pedido.statusPagamento === 'Pago' ? '#065f46' : '#92400e',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          display: 'inline-block'
                        }}>
                          {entrega.pedido.statusPagamento === 'Pago' ? '✅ Pago' : '⏳ ' + entrega.pedido.statusPagamento}
                        </p>
                      )}
                      {entrega.pedido?.valorPago && entrega.pedido?.valorPago > 0 && (
                        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                          Valor Pago: R$ {entrega.pedido.valorPago.toFixed(2)}
                          {entrega.pedido?.valorRestante > 0 && (
                            <span style={{ color: '#ef4444' }}> | Restante: R$ {entrega.pedido.valorRestante.toFixed(2)}</span>
                          )}
                        </p>
                      )}
                      {entrega.status === 'aceita' && (
                        <button
                          className="btn btn-primary"
                          onClick={() => atualizarStatusMutation.mutate({ entregaId: entrega._id, status: 'saiu' })}
                          disabled={atualizarStatusMutation.isLoading}
                        >
                          🚚 Saiu para Entrega
                        </button>
                      )}
                      {entrega.status === 'saiu' && (
                        <button
                          className="btn btn-success"
                          onClick={() => atualizarStatusMutation.mutate({ entregaId: entrega._id, status: 'chegou' })}
                          disabled={atualizarStatusMutation.isLoading}
                        >
                          Chegou no Endereço
                        </button>
                      )}
                      {entrega.status === 'chegou' && entrega.pedido?.statusPagamento !== 'Pago' && (
                        <button
                          className="btn btn-primary"
                          onClick={() => setModalPagamento(entrega)}
                          disabled={registrarPagamentoMutation.isLoading}
                        >
                          Registrar Pagamento
                        </button>
                      )}
                      {entrega.status === 'chegou' && entrega.pedido?.statusPagamento === 'Pago' && (
                        <p style={{ 
                          marginTop: '0.5rem', 
                          padding: '0.5rem', 
                          borderRadius: '0.25rem',
                          background: '#d1fae5',
                          color: '#065f46',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          textAlign: 'center'
                        }}>
                          ✅ Pagamento Registrado
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <Package size={64} />
                <p>Nenhuma entrega no momento</p>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'historico' && (
        <HistoricoDiarias entregador={entregador} />
      )}


      {/* Modal de Pagamento */}
      {modalPagamento && (
        <div className="modal-overlay" onClick={() => setModalPagamento(null)}>
          <div className="modal-content modal-pagamento" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>💳 Registrar Pagamento</h3>
              <button className="modal-close" onClick={() => setModalPagamento(null)}>×</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <p><strong>Pedido:</strong> #{modalPagamento.pedido?.numero}</p>
              <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1rem' }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>Total do Pedido</p>
                {modalPagamento.pedido?.taxaEntrega > 0 && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Subtotal:</span>
                      <span>R$ {(modalPagamento.pedido.total - modalPagamento.pedido.taxaEntrega).toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                      <span>Taxa de Entrega:</span>
                      <span>R$ {modalPagamento.pedido.taxaEntrega.toFixed(2)}</span>
                    </div>
                  </div>
                )}
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
                  R$ {modalPagamento.pedido?.total?.toFixed(2)}
                </p>
                {pagamentosSequenciais.length > 0 && (
                  <>
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
                      Valor Pago: R$ {pagamentosSequenciais.reduce((acc, p) => acc + p.valor, 0).toFixed(2)}
                    </p>
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#ef4444', fontWeight: 600 }}>
                      Restante: R$ {(modalPagamento.pedido?.total - pagamentosSequenciais.reduce((acc, p) => acc + p.valor, 0)).toFixed(2)}
                    </p>
                  </>
                )}
              </div>

              {/* ✅ NOVO: Lista de pagamentos sequenciais */}
              {pagamentosSequenciais.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ marginBottom: '0.75rem', fontSize: '1rem', fontWeight: 600 }}>Pagamentos Adicionados:</h4>
                  {pagamentosSequenciais.map((pag, idx) => {
                    const formaPagamentoTexto = {
                      'dinheiro': '💵 Dinheiro',
                      'pix': '📱 PIX',
                      'cartao_credito': '💳 Cartão de Crédito',
                      'cartao_debito': '💳 Cartão de Débito'
                    }[pag.tipo] || pag.tipo;
                    
                    return (
                      <div key={idx} style={{ 
                        background: '#f3f4f6', 
                        padding: '0.75rem', 
                        borderRadius: '0.5rem', 
                        marginBottom: '0.5rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600 }}>
                            {formaPagamentoTexto}: R$ {pag.valor.toFixed(2)}
                          </p>
                          {pag.tipo === 'dinheiro' && pag.troco > 0 && (
                            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#059669' }}>
                              Troco: R$ {pag.troco.toFixed(2)}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => setPagamentosSequenciais(pagamentosSequenciais.filter((_, i) => i !== idx))}
                          style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.25rem',
                            padding: '0.25rem 0.5rem',
                            cursor: 'pointer',
                            fontSize: '0.75rem'
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ✅ NOVO: Adicionar novo pagamento sequencial */}
              <div style={{ 
                border: '2px dashed #d1d5db', 
                padding: '1rem', 
                borderRadius: '0.5rem', 
                marginBottom: '1rem',
                background: pagamentosSequenciais.length > 0 ? '#fef3c7' : 'transparent'
              }}>
                <h4 style={{ marginBottom: '0.75rem', fontSize: '1rem', fontWeight: 600 }}>
                  {pagamentosSequenciais.length > 0 ? '➕ Adicionar Outro Pagamento' : '💳 Forma de Pagamento'}
                </h4>
                
                <div className="form-group">
                  <label>Forma de Pagamento <span style={{ color: '#ef4444' }}>*</span></label>
                  <select
                    value={novoPagamento.tipo}
                    onChange={(e) => {
                      const novoTipo = e.target.value;
                      const total = modalPagamento.pedido?.total || 0;
                      const valorPago = pagamentosSequenciais.reduce((acc, p) => acc + p.valor, 0);
                      const valorRestante = total - valorPago;
                      setNovoPagamento({
                        tipo: novoTipo,
                        valor: valorRestante,
                        valorRecebido: novoTipo === 'dinheiro' ? (novoPagamento.valorRecebido || valorRestante) : valorRestante,
                        troco: 0
                      });
                    }}
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '2px solid #e5e7eb', fontSize: '1rem' }}
                  >
                    <option value="dinheiro">💵 Dinheiro</option>
                    <option value="pix">📱 PIX</option>
                    <option value="cartao_credito">💳 Cartão de Crédito</option>
                    <option value="cartao_debito">💳 Cartão de Débito</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Valor <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={modalPagamento.pedido?.total - pagamentosSequenciais.reduce((acc, p) => acc + p.valor, 0)}
                    value={novoPagamento.valor}
                    onChange={(e) => {
                      const valor = parseFloat(e.target.value) || 0;
                      setNovoPagamento({
                        ...novoPagamento,
                        valor: valor,
                        valorRecebido: novoPagamento.tipo === 'dinheiro' ? (novoPagamento.valorRecebido || valor) : valor
                      });
                    }}
                    placeholder="0.00"
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '2px solid #e5e7eb', fontSize: '1rem' }}
                  />
                </div>

                {novoPagamento.tipo === 'dinheiro' && (
                  <>
                    <div className="form-group">
                      <label>Valor Recebido <span style={{ color: '#ef4444' }}>*</span></label>
                      <input
                        type="number"
                        step="0.01"
                        min={novoPagamento.valor}
                        value={novoPagamento.valorRecebido}
                        onChange={(e) => {
                          const valor = parseFloat(e.target.value) || novoPagamento.valor;
                          setNovoPagamento({
                            ...novoPagamento,
                            valorRecebido: valor,
                            troco: Math.max(0, valor - novoPagamento.valor)
                          });
                        }}
                        placeholder="0.00"
                        style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '2px solid #e5e7eb', fontSize: '1rem' }}
                      />
                    </div>
                    
                    {novoPagamento.troco > 0 && (
                      <div style={{ background: '#d1fae5', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '0.75rem', border: '2px solid #10b981' }}>
                        <p style={{ margin: 0, fontSize: '0.875rem', color: '#065f46', fontWeight: 600 }}>
                          💵 Troco: R$ {novoPagamento.troco.toFixed(2)}
                        </p>
                      </div>
                    )}
                  </>
                )}

                {pagamentosSequenciais.length > 0 && (
                  <button
                    onClick={() => {
                      const total = modalPagamento.pedido?.total || 0;
                      const valorPago = pagamentosSequenciais.reduce((acc, p) => acc + p.valor, 0);
                      const valorRestante = total - valorPago;
                      
                      if (!novoPagamento.tipo || !novoPagamento.valor || novoPagamento.valor <= 0) {
                        toast.error('Informe o tipo e valor do pagamento!');
                        return;
                      }
                      
                      if (novoPagamento.valor > valorRestante) {
                        toast.error(`Valor excede o restante: R$ ${valorRestante.toFixed(2)}`);
                        return;
                      }
                      
                      let troco = 0;
                      if (novoPagamento.tipo === 'dinheiro' && novoPagamento.valorRecebido > novoPagamento.valor) {
                        troco = novoPagamento.valorRecebido - novoPagamento.valor;
                      }
                      
                      setPagamentosSequenciais([...pagamentosSequenciais, {
                        ...novoPagamento,
                        troco: troco
                      }]);
                      
                      const totalRestante = total - valorPago - novoPagamento.valor;
                      setNovoPagamento({ tipo: 'dinheiro', valor: totalRestante > 0 ? totalRestante : 0, valorRecebido: totalRestante > 0 ? totalRestante : 0, troco: 0 });
                    }}
                    disabled={!novoPagamento.tipo || !novoPagamento.valor || novoPagamento.valor <= 0}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: 'none',
                      background: '#3b82f6',
                      color: 'white',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      marginTop: '0.5rem',
                      opacity: (!novoPagamento.tipo || !novoPagamento.valor || novoPagamento.valor <= 0) ? 0.5 : 1
                    }}
                  >
                    ➕ Adicionar Pagamento
                  </button>
                )}
              </div>

              {/* ✅ Formato antigo (compatibilidade) - só aparece se não houver pagamentos sequenciais */}
              {pagamentosSequenciais.length === 0 && (
                <>
                  <div className="form-group">
                    <label>Valor Recebido (R$)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={pagamentoData.valorRecebido}
                      onChange={(e) => setPagamentoData({ ...pagamentoData, valorRecebido: e.target.value })}
                      placeholder="0.00"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Forma de Pagamento</label>
                    <select
                      value={pagamentoData.formaPagamentoRecebida}
                      onChange={(e) => setPagamentoData({ ...pagamentoData, formaPagamentoRecebida: e.target.value })}
                    >
                      <option value="dinheiro">Dinheiro</option>
                      <option value="pix">PIX</option>
                      <option value="cartao_credito">Cartão Crédito</option>
                      <option value="cartao_debito">Cartão Débito</option>
                    </select>
                  </div>

                  <div style={{ 
                    background: '#dbeafe', 
                    padding: '0.75rem', 
                    borderRadius: '0.5rem', 
                    marginBottom: '1rem',
                    border: '2px solid #3b82f6',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    const total = modalPagamento.pedido?.total || 0;
                    // Adicionar o primeiro pagamento automaticamente com o total
                    setPagamentosSequenciais([{
                      tipo: 'dinheiro',
                      valor: total,
                      valorRecebido: total,
                      troco: 0
                    }]);
                    setNovoPagamento({ tipo: 'dinheiro', valor: 0, valorRecebido: 0, troco: 0 });
                  }}
                  >
                    <p style={{ margin: 0, fontSize: '0.875rem', color: '#1e40af', fontWeight: 600 }}>
                      💡 Clique aqui para dividir o pagamento em múltiplas formas
                    </p>
                  </div>
                </>
              )}

              <div className="modal-actions">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setModalPagamento(null);
                    setPagamentosSequenciais([]);
                    setNovoPagamento({ tipo: 'dinheiro', valor: 0, valorRecebido: 0, troco: 0 });
                  }}
                >
                  Cancelar
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    const total = modalPagamento.pedido?.total || 0;
                    
                    // Se houver pagamentos sequenciais, usar eles
                    if (pagamentosSequenciais.length > 0) {
                      const valorPago = pagamentosSequenciais.reduce((acc, p) => acc + p.valor, 0);
                      
                      if (valorPago < total) {
                        toast.error(`Pagamento incompleto. Faltam R$ ${(total - valorPago).toFixed(2)}`);
                        return;
                      }
                      
                      registrarPagamentoMutation.mutate({
                        entregaId: modalPagamento._id,
                        data: {
                          pagamentos: pagamentosSequenciais
                        }
                      });
                    } else {
                      // Formato antigo (compatibilidade)
                      if (!pagamentoData.valorRecebido || parseFloat(pagamentoData.valorRecebido) <= 0) {
                        toast.error('Digite um valor válido');
                        return;
                      }
                      registrarPagamentoMutation.mutate({
                        entregaId: modalPagamento._id,
                        data: {
                          valorRecebido: parseFloat(pagamentoData.valorRecebido),
                          formaPagamentoRecebida: pagamentoData.formaPagamentoRecebida
                        }
                      });
                    }
                  }}
                  disabled={registrarPagamentoMutation.isLoading}
                >
                  {registrarPagamentoMutation.isLoading ? 'Registrando...' : 'Confirmar Pagamento'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Iniciar Diária */}
      {modalIniciarDiaria && (
        <div className="modal-overlay" onClick={() => setModalIniciarDiaria(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>🚀 Iniciar Diária</h3>
              <button className="modal-close" onClick={() => setModalIniciarDiaria(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '1rem', color: '#4a5568' }}>
                Digite o KM atual do seu veículo para iniciar sua diária de trabalho.
              </p>
              <div className="form-group">
                <label>KM de Entrada</label>
                <input
                  type="number"
                  value={kmEntradaInicial}
                  onChange={(e) => setKmEntradaInicial(e.target.value)}
                  placeholder="Ex: 15000"
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: '0.5rem',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setModalIniciarDiaria(false);
                  setKmEntradaInicial('');
                }}
              >
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={handleIniciarDiaria}
                disabled={registrarKMMutation.isLoading}
              >
                {registrarKMMutation.isLoading ? 'Iniciando...' : 'Confirmar Início'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Encerrar Diária */}
      {modalEncerrarDiaria && (
        <div className="modal-overlay" onClick={() => setModalEncerrarDiaria(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>🏁 Encerrar Diária</h3>
              <button className="modal-close" onClick={() => setModalEncerrarDiaria(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>KM de Saída</label>
                <input
                  type="number"
                  value={kmSaidaDiaria}
                  onChange={(e) => setKmSaidaDiaria(e.target.value)}
                  placeholder="Ex: 15100"
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: '0.5rem',
                    fontSize: '1rem'
                  }}
                />
              </div>
              {registroKM?.kmEntrada && (
                <div className="km-preview" style={{ padding: '1rem', background: '#f3f4f6', borderRadius: '8px', marginTop: '1rem' }}>
                  <p><strong>KM Entrada:</strong> {registroKM.kmEntrada}</p>
                  <p><strong>KM Rodados (estimado):</strong> {kmSaidaDiaria ? Math.max(0, parseFloat(kmSaidaDiaria) - registroKM.kmEntrada) : 0} km</p>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setModalEncerrarDiaria(false);
                  setKmSaidaDiaria('');
                }}
              >
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={handleEncerrarDiaria}
                disabled={encerrarDiariaMutation.isLoading}
              >
                {encerrarDiariaMutation.isLoading ? 'Encerrando...' : 'Confirmar Encerramento'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
      {/* Fim Main Content */}
    </div>
  );
};

// Componente de Histórico de Diárias
const HistoricoDiarias = ({ entregador }) => {
  const { data: diarias, isLoading } = useQuery(
    ['diarias-entregador', entregador?._id],
    async () => {
      if (!entregador?._id) return { diarias: [] };
      try {
        const response = await api.get('/api/entregadores/me/diarias');
        return response.data.data || { diarias: [] };
      } catch (error) {
        console.error('Erro ao buscar diárias:', error);
        return { diarias: [] };
      }
    },
    {
      enabled: !!entregador?._id,
      refetchInterval: 60000 // Atualizar a cada minuto
    }
  );

  if (isLoading) {
    return (
      <div className="entregador-section" style={{ textAlign: 'center', padding: '3rem' }}>
        <RefreshCw size={48} className="spinning" style={{ animation: 'spin 1s linear infinite' }} />
        <p style={{ marginTop: '1rem', color: '#718096' }}>Carregando histórico...</p>
      </div>
    );
  }

  const listaDiarias = diarias?.diarias || [];

  if (listaDiarias.length === 0) {
    return (
      <div className="entregador-section">
        <h3>📜 Histórico Financeiro</h3>
        <div className="empty-state">
          <History size={64} />
          <p>Nenhuma diária encontrada</p>
          <p style={{ fontSize: '0.9rem', color: '#718096', marginTop: '8px' }}>
            Quando você encerrar diárias, elas aparecerão aqui
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="entregador-section">
      <h3>📜 Histórico Financeiro</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
        {listaDiarias.map((diaria) => (
          <div
            key={diaria._id}
            style={{
              padding: '1.5rem',
              background: diaria.status === 'pago' ? '#f0fdf4' : '#fef3c7',
              borderRadius: '8px',
              border: `2px solid ${diaria.status === 'pago' ? '#10b981' : '#f59e0b'}`,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
              <div>
                <h4 style={{ margin: 0, color: '#1f2937', fontSize: '1.25rem' }}>
                  {new Date(diaria.data).toLocaleDateString('pt-BR', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </h4>
                <p style={{ margin: '0.25rem 0 0 0', color: '#64748b', fontSize: '0.875rem' }}>
                  {diaria.quantidadeEntregas || 0} entregas realizadas
                </p>
              </div>
              <span
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  background: diaria.status === 'pago' ? '#d1fae5' : '#fef3c7',
                  color: diaria.status === 'pago' ? '#10b981' : '#f59e0b'
                }}
              >
                {diaria.status === 'pago' ? '✅ Pago' : '⏳ Pendente'}
              </span>
            </div>

            {/* Informações de KM */}
            <div style={{ 
              padding: '1rem', 
              background: '#ffffff', 
              borderRadius: '6px', 
              marginBottom: '1rem',
              border: '1px solid #e5e7eb'
            }}>
              <h5 style={{ margin: '0 0 0.75rem 0', color: '#374151', fontSize: '1rem' }}>📊 Informações de KM</h5>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '0.875rem' }}>
                <div>
                  <span style={{ color: '#64748b' }}>KM Entrada:</span>
                  <strong style={{ display: 'block', color: '#1f2937', marginTop: '0.25rem' }}>
                    {diaria.kmEntrada || 0} km
                  </strong>
                </div>
                <div>
                  <span style={{ color: '#64748b' }}>KM Saída:</span>
                  <strong style={{ display: 'block', color: '#1f2937', marginTop: '0.25rem' }}>
                    {diaria.kmSaida || 0} km
                  </strong>
                </div>
                <div>
                  <span style={{ color: '#64748b' }}>KM Rodados:</span>
                  <strong style={{ display: 'block', color: '#1f2937', marginTop: '0.25rem' }}>
                    {diaria.kmRodados || 0} km
                  </strong>
                </div>
                <div>
                  <span style={{ color: '#64748b' }}>Valor KM:</span>
                  <strong style={{ display: 'block', color: '#1f2937', marginTop: '0.25rem' }}>
                    R$ {(diaria.valorKM || 0).toFixed(2)}
                  </strong>
                </div>
              </div>
            </div>

            {/* Valores Financeiros */}
            <div style={{ 
              padding: '1rem', 
              background: '#ffffff', 
              borderRadius: '6px',
              border: '1px solid #e5e7eb'
            }}>
              <h5 style={{ margin: '0 0 0.75rem 0', color: '#374151', fontSize: '1rem' }}>💰 Valores</h5>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b' }}>Valor da Diária:</span>
                  <strong style={{ color: '#1f2937' }}>R$ {(diaria.valorDiaria || 0).toFixed(2)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b' }}>Valor do KM:</span>
                  <strong style={{ color: '#1f2937' }}>R$ {(diaria.valorKM || 0).toFixed(2)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b' }}>Comissões:</span>
                  <strong style={{ color: '#1f2937' }}>R$ {(diaria.totalComissoes || 0).toFixed(2)}</strong>
                </div>
                <div style={{ 
                  marginTop: '0.5rem', 
                  paddingTop: '0.75rem', 
                  borderTop: '2px solid #e5e7eb',
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ color: '#1f2937', fontWeight: 'bold', fontSize: '1.1rem' }}>Total a Receber:</span>
                  <strong style={{ fontSize: '1.5rem', color: '#0369a1', fontWeight: 'bold' }}>
                    R$ {(diaria.totalReceber || 0).toFixed(2)}
                  </strong>
                </div>
              </div>
            </div>

            {/* Informações de Pagamento */}
            {diaria.status === 'pago' && diaria.dataPagamento && (
              <div style={{ 
                marginTop: '1rem', 
                padding: '0.75rem', 
                background: '#d1fae5', 
                borderRadius: '6px',
                textAlign: 'center',
                fontSize: '0.875rem',
                color: '#065f46'
              }}>
                ✅ Pago em {new Date(diaria.dataPagamento).toLocaleDateString('pt-BR')}
                {diaria.formaPagamento && ` via ${diaria.formaPagamento}`}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Dashboard;

